<template>
  <section class="stats" ref="statsEl">
    <div class="container">
      <h2>{{ t('stats.title') }}</h2>
      <div class="stats-grid">
        <div class="stat">
          <div class="number yellow" v-html="t('stats.stat1.number')"></div>
          <div class="label">{{ t('stats.stat1.label') }}</div>
        </div>
        <div class="stat">
          <div class="number" v-html="t('stats.stat2.number')"></div>
          <div class="label">{{ t('stats.stat2.label') }}</div>
        </div>
        <div class="stat">
          <div class="number yellow" v-html="t('stats.stat3.number')"></div>
          <div class="label">{{ t('stats.stat3.label') }}</div>
        </div>
        <div class="stat stat-live">
          <div class="number gold">{{ formatCount(counters ? counters.berekeningen : 0) }}</div>
          <div class="label">{{ t('stats.stat4.label', { kopieen: formatCount(counters ? counters.kopieen : 0) }) }}</div>
        </div>
      </div>
      <p class="stats-source" v-html="t('stats.source')"></p>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useLocale } from '../composables/useLocale'

const { t } = useLocale()

const statsEl = ref(null)
const counters = ref(null)
let observer = null

onMounted(async () => {
  if (statsEl.value) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible')
          observer.unobserve(entry.target)
        }
      })
    }, { threshold: 0.3 })
    observer.observe(statsEl.value)
  }

  try {
    const res = await fetch('/api/counters')
    const data = await res.json()
    if (data.berekeningen > 0 || data.kopieen > 0) {
      counters.value = data
    }
  } catch { /* ignore */ }
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

function formatCount(n) {
  return n.toLocaleString('nl-NL')
}
</script>

<style scoped>
.stats {
  background: var(--blue-darker);
  color: var(--bg);
  padding: 80px 0;
  position: relative;
  overflow: hidden;
}
@media (max-width: 768px) { .stats { padding: 48px 0; } }
.stats h2 {
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--accent-light);
  font-weight: 600;
  margin-bottom: 56px;
  text-align: center;
}
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
}
@media (max-width: 900px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 500px) { .stats-grid { grid-template-columns: 1fr; } }
.stat { text-align: center; padding: 0 12px; }
.stat .number {
  font-family: 'Fraunces', serif;
  font-size: clamp(40px, 5vw, 72px);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  margin-bottom: 16px;
  color: var(--bg);
}
.stat .number.yellow { color: var(--accent-light); }
.stat .number.gold { color: var(--yellow); }
.stat-live {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 24px 12px;
  margin: -24px 0;
}
.stat .number :deep(.small) { font-size: 0.5em; vertical-align: super; }
.stat .label {
  font-size: 16px;
  max-width: 26ch;
  margin: 0 auto;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
}
.stats-source {
  text-align: center;
  margin-top: 48px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.stats-source :deep(a) {
  color: rgba(255, 255, 255, 0.7);
  text-decoration-color: rgba(255, 255, 255, 0.3);
}
.stats-source :deep(a:hover) {
  color: #fff;
  text-decoration-color: #fff;
}

.stats.visible .stat {
  animation: popIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
}
.stats.visible .stat:nth-child(1) { animation-delay: 0.1s; }
.stats.visible .stat:nth-child(2) { animation-delay: 0.25s; }
.stats.visible .stat:nth-child(3) { animation-delay: 0.4s; }
.stats.visible .stat:nth-child(4) { animation-delay: 0.55s; }
</style>
